import React from "react";
import { Helmet } from "react-helmet-async";
import { BUSINESS } from "@/data/content";

const SITE = "https://roofing-goral.com";
const PREVIEW_HOSTS = ["preview.emergentagent.com", "localhost"];

function isPreview() {
  if (typeof window === "undefined") return false;
  return PREVIEW_HOSTS.some(h => window.location.hostname.includes(h));
}

export function SEO({ title, description, path = "", image, children, schema, noindex }) {
  // Keep titles short for SERP truncation (~60 chars). Add brand suffix only when the
  // title doesn't already include "|" (custom separator) or our brand name.
  const hasBrand = title.includes("Goral");
  const hasSeparator = title.includes(" | ");
  const fullTitle = (hasBrand || hasSeparator) ? title : `${title} | Goral Construction`;
  // Clamp meta description to ~160 chars for SERP truncation
  const metaDesc = description && description.length > 160 ? description.slice(0, 157).trimEnd() + "…" : (description || "");
  // Canonical URL — always with trailing slash for non-root routes to match
  // Netlify's pretty-URL default (matches prerender + sitemap).
  const url = path === "/" ? `${SITE}/` : `${SITE}${path.replace(/\/+$/, "")}/`;
  const ogImage = image || `${SITE}/projects/p01_0_d094d2b1.jpg`;
  const robots = (noindex || isPreview()) ? "noindex, nofollow" : "index, follow";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={robots} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Goral Construction LLC" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={ogImage} />
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
      {children}
    </Helmet>
  );
}

// Global business schema (RoofingContractor + LocalBusiness + HomeAndConstructionBusiness) - injected once in App
export const businessSchema = {
  "@context": "https://schema.org",
  "@type": ["RoofingContractor", "LocalBusiness", "HomeAndConstructionBusiness"],
  "@id": `${SITE}/#business`,
  name: BUSINESS.name,
  alternateName: "Goral Construction",
  slogan: "Family-owned roofing in Oakland County since 2010. Done right the first time.",
  url: SITE,
  telephone: BUSINESS.phone,
  email: BUSINESS.email,
  priceRange: "$$",
  image: `${SITE}/projects/p01_0_d094d2b1.jpg`,
  logo: "https://roofing-goral.com/android-chrome-512x512.png",
  paymentAccepted: ["Cash", "Check", "Credit Card", "Financing"],
  knowsLanguage: ["en", "pl"],
  knowsAbout: [
    "Roof Replacement",
    "Roof Repair",
    "Storm Damage Restoration",
    "Insurance Claim Assistance",
    "Asphalt Shingles",
    "Metal Roofing",
    "Ice Dam Prevention",
    "Siding Installation",
    "Seamless Gutters",
    "Skylight Installation",
  ],
  award: [
    "Master Shingle Applicator (Wizard Level)",
    "ShingleMaster Credential",
    "VELUX Certified Skylight Installer",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: BUSINESS.phoneTel || "+1-248-804-6933",
      contactType: "customer service",
      areaServed: "US-MI",
      availableLanguage: ["English", "Polish"],
      hoursAvailable: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          opens: "07:00",
          closes: "19:00",
        },
      ],
    },
    {
      "@type": "ContactPoint",
      telephone: BUSINESS.phoneTel || "+1-248-804-6933",
      contactType: "emergency",
      areaServed: "US-MI",
      availableLanguage: ["English", "Polish"],
      // 24/7 storm damage & active leak response is a distinct labeled service,
      // not part of the standard opening hours (per SEO brief 2026-02 Task 2).
      hoursAvailable: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          opens: "00:00",
          closes: "23:59",
        },
      ],
      description: "24/7 Emergency Storm Damage & Active Leak Response",
    },
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: BUSINESS.address.street,
    addressLocality: BUSINESS.address.city,
    addressRegion: BUSINESS.address.state,
    postalCode: BUSINESS.address.zip,
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: BUSINESS.geo.lat, longitude: BUSINESS.geo.lng },
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], opens: "07:00", closes: "19:00" },
  ],
  foundingDate: String(BUSINESS.founded),
  founders: BUSINESS.owners.map(name => ({ "@type": "Person", name })),
  sameAs: [
    BUSINESS.social.googleBusiness,
    BUSINESS.social.google,
    BUSINESS.social.angi,
    BUSINESS.social.facebook,
    BUSINESS.social.yelp,
  ].filter(Boolean),
  areaServed: [
    { "@type": "AdministrativeArea", name: "Oakland County, Michigan" },
    ...["Waterford","Clarkston","White Lake","Commerce Township","West Bloomfield","Bloomfield Hills","Rochester Hills","Troy","Auburn Hills","Birmingham","Pontiac","Royal Oak","Lake Orion"].map(n => ({ "@type": "City", name: `${n}, MI` })),
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: BUSINESS.rating.value,
    reviewCount: BUSINESS.rating.count,
    ratingCount: BUSINESS.rating.count,
    bestRating: 5,
    worstRating: 1,
  },
  // Individual Review objects — required for Google to surface star snippets
  // in SERP. AggregateRating alone is no longer sufficient; Google's 2023+
  // structured-data guidelines explicitly require the `review` array.
  // `itemReviewed` MUST include address per schema.org / Semrush spec.
  review: (() => {
    const reviewedRef = {
      "@type": "RoofingContractor",
      name: BUSINESS.name,
      address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS.address.street,
        addressLocality: BUSINESS.address.city,
        addressRegion: BUSINESS.address.state,
        postalCode: BUSINESS.address.zip,
        addressCountry: "US",
      },
      telephone: BUSINESS.phone,
      url: SITE,
    };
    return [
      { "@type": "Review", author: { "@type": "Person", name: "Lisa Angona" }, datePublished: "2026-03-15", reviewRating: { "@type": "Rating", ratingValue: 5, bestRating: 5 }, reviewBody: "My roof looks amazing. Working with Matt was a wonderful experience. He is very professional, no hidden cost. His estimate was lower than three other companies.", itemReviewed: reviewedRef },
      { "@type": "Review", author: { "@type": "Person", name: "Michelle M." }, datePublished: "2026-03-08", reviewRating: { "@type": "Rating", ratingValue: 5, bestRating: 5 }, reviewBody: "Very professional, fair negotiation, showed when they said they would. Very happy with the work on our Waterford home.", itemReviewed: reviewedRef },
      { "@type": "Review", author: { "@type": "Person", name: "Steeevee R." }, datePublished: "2026-03-02", reviewRating: { "@type": "Rating", ratingValue: 5, bestRating: 5 }, reviewBody: "Matt is the best around by far. He and his crew came out and took great care of my roof. Highly recommend, 10 out of 10.", itemReviewed: reviewedRef },
      { "@type": "Review", author: { "@type": "Person", name: "Lucyna O." }, datePublished: "2025-05-12", reviewRating: { "@type": "Rating", ratingValue: 5, bestRating: 5 }, reviewBody: "My roof was very old and in really bad shape. Goral responded immediately and did an excellent job. Their professionalism and high-quality work are hard to find these days.", itemReviewed: reviewedRef },
      { "@type": "Review", author: { "@type": "Person", name: "Olivia C." }, datePublished: "2025-05-04", reviewRating: { "@type": "Rating", ratingValue: 5, bestRating: 5 }, reviewBody: "He did an amazing job. Quick to quote, quick to do the work. Went above and beyond what I asked for. Will definitely hire again.", itemReviewed: reviewedRef },
    ];
  })(),
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Roofing & Exterior Services",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Residential Roofing" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Roof Repair" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Siding Installation" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Seamless Gutters" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Insurance Claim Assistance" } },
    ],
  },
};

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE}${it.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  // Speakable: helps Google Assistant / voice answers pick FAQ Q&A pairs.
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: ["h2", "h3", "[data-speakable]"],
  },
  mainEntity: faqs.map(f => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const serviceSchema = ({ name, description, path, parent }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  url: path === "/" ? `${SITE}/` : `${SITE}${path.replace(/\/+$/, "")}/`,
  // Full inline provider block — Semrush + Google Rich Results treat
  // `@id`-only references as "missing address" for the Service.provider
  // node. Inline the full NAP from Google Business Profile so every
  // Service page has a self-contained, validation-passing schema.
  provider: {
    "@type": "RoofingContractor",
    "@id": `${SITE}/#business`,
    name: BUSINESS.name,
    url: `${SITE}/`,
    telephone: BUSINESS.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.address.street,
      addressLocality: BUSINESS.address.city,
      addressRegion: BUSINESS.address.state,
      postalCode: BUSINESS.address.zip,
      addressCountry: "US",
    },
    areaServed: [
      { "@type": "AdministrativeArea", name: "Oakland County, Michigan" },
      { "@type": "State", name: "Michigan" },
    ],
  },
  areaServed: { "@type": "AdministrativeArea", name: "Oakland County, Michigan" },
  ...(parent ? { isRelatedTo: { "@type": "Service", name: parent } } : {}),
});

export const articleSchema = ({ title, description, path, image, date, read }) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "@id": `${SITE}${path}#article`,
  headline: title.length > 110 ? title.slice(0, 107) + "..." : title,
  description,
  image: image?.startsWith("http") ? image : `${SITE}${image}`,
  datePublished: date,
  dateModified: new Date().toISOString().slice(0, 10),
  author: {
    "@type": "Person",
    name: "Maciej Ogonowski",
    jobTitle: "Owner & Master Shingle Applicator (Wizard Level)",
    worksFor: { "@id": `${SITE}/#business` },
    url: `${SITE}/about`,
  },
  publisher: { "@id": `${SITE}/#business` },
  mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}${path}` },
  timeRequired: read,
  inLanguage: "en-US",
  about: [
    { "@type": "Thing", name: "Roofing" },
    { "@type": "Place", name: "Oakland County, Michigan" },
  ],
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: ["h1", ".lead", ".bottom-line"],
  },
});

export const reviewsSchema = (reviews) => {
  const itemReviewed = {
    "@type": "RoofingContractor",
    "@id": `${SITE}/#business`,
    name: BUSINESS.name,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.address.street,
      addressLocality: BUSINESS.address.city,
      addressRegion: BUSINESS.address.state,
      postalCode: BUSINESS.address.zip,
      addressCountry: "US",
    },
    telephone: BUSINESS.phone,
    url: SITE,
  };
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["RoofingContractor", "LocalBusiness"],
        "@id": `${SITE}/#business`,
        name: BUSINESS.name,
        url: SITE,
        telephone: BUSINESS.phone,
        image: `${SITE}/brand/maciej-roof.jpg`,
        address: {
          "@type": "PostalAddress",
          streetAddress: BUSINESS.address.street,
          addressLocality: BUSINESS.address.city,
          addressRegion: BUSINESS.address.state,
          postalCode: BUSINESS.address.zip,
          addressCountry: "US",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: BUSINESS.rating.value,
          reviewCount: BUSINESS.rating.count,
          bestRating: 5,
          worstRating: 1,
        },
      },
      ...reviews.map((r, i) => ({
        "@type": "Review",
        "@id": `${SITE}/reviews#review-${i + 1}`,
        itemReviewed,
        reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
        author: { "@type": "Person", name: r.name },
        datePublished: r.date,
        reviewBody: r.quote,
        publisher: { "@type": "Organization", name: "Angi" },
      })),
    ],
  };
};
